import {
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@daily/shared/components/Tabs';
import { ThemeScope, useTheme } from '@daily/shared/contexts/Theme';
import { hexToRgba } from '@daily/shared/lib/colors';
import { ScreenShare } from '@daily-co/daily-react';
import classNames from 'classnames';
import React, { useCallback, useId, useRef } from 'react';

import { CustomIntegration } from '../../hooks/useCustomIntegrations';
import { usePinnedId } from '../../lib/state/layout';
import { MainIntegration } from './MainIntegration';
import { MainIntegrationTab } from './MainIntegrationTab';
import { ScreenPinTile } from './ScreenPinTile';
import { ScreenTab } from './ScreenTab';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  integrations?: CustomIntegration[];
  screens?: ScreenShare[];
}

export const SharedTabs: React.FC<Props> = ({
  className,
  integrations = [],
  screens = [],
  ...props
}) => {
  const { colors } = useTheme();
  const [pinnedId] = usePinnedId();

  const tabsId = useId();

  const tabs: (CustomIntegration | ScreenShare)[] = [
    ...integrations,
    ...screens,
  ];

  const pinnedIdx = tabs.findIndex((t) =>
    'screenId' in t ? t.screenId === pinnedId : t.id === pinnedId
  );

  const lastActiveTab = useRef<number>(0);

  const handleTabChange = useCallback((idx: number) => {
    lastActiveTab.current = idx;
  }, []);

  return (
    <div className={classNames('shared-tabs', className)} {...props}>
      <Tabs
        id={tabsId}
        initialIndex={pinnedIdx >= 0 ? pinnedIdx : lastActiveTab.current}
        onChangeTab={handleTabChange}
      >
        <ThemeScope theme="dark">
          <TabList gap={16} spaceAround={8}>
            {tabs
              .filter((t) => {
                if (!pinnedId) return true;
                if (
                  !tabs.some((t) =>
                    'screenId' in t
                      ? t.screenId === pinnedId
                      : t.id === pinnedId
                  )
                )
                  return true;
                if ('screenId' in t) return t.screenId === pinnedId;
                return t.id === pinnedId;
              })
              .map((t, idx) => {
                if ('screenId' in t) {
                  return (
                    <ScreenTab
                      key={t.screenId}
                      index={pinnedIdx >= 0 ? pinnedIdx : idx}
                      screen={t}
                    />
                  );
                } else {
                  return (
                    <MainIntegrationTab
                      key={t.id}
                      index={pinnedIdx >= 0 ? pinnedIdx : idx}
                      integration={t}
                    />
                  );
                }
              })}
          </TabList>
        </ThemeScope>
        <TabPanels>
          {tabs.map((t, idx) => (
            <TabPanel
              key={'screenId' in t ? t.screenId : t.id}
              className={classNames('panel', {
                'screen-panel': 'screenId' in t,
              })}
              hideStrategy="css"
              index={idx}
            >
              {'screenId' in t ? (
                <ScreenPinTile
                  fitToRatio
                  hideActions
                  isScreen
                  sessionId={t.session_id}
                />
              ) : (
                <MainIntegration integration={t} />
              )}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
      <style jsx>{`
        .shared-tabs {
          display: flex;
          flex-direction: column;
          height: 100%;
          width: 100%;
        }
        .shared-tabs :global(.tablist) {
          --tab-bg: ${colors.custom.mainAreaBgAccent};
          --tabs-scroll-bg-op0: ${hexToRgba(colors.custom.mainAreaBgAccent, 0)};
          background-color: ${colors.custom.mainAreaBgAccent};
        }
        .shared-tabs :global(.tab) {
          --pad: 0px;
        }
        .shared-tabs :global(.tablist .scroll) {
          --pad: 4px;
        }
        .tab-content {
          display: flex;
          gap: 4px;
        }
        .shared-tabs :global(.panel) {
          flex: 1;
          padding-top: 0;
          position: relative;
        }
        .shared-tabs :global(.screen-panel.visible) {
          align-items: center;
          display: flex;
          justify-content: center;
        }
      `}</style>
    </div>
  );
};
